import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { TRADES, COUNTIES, DURATIONS } from "@/constants/data";
import { MapPin, Clock, Euro, Briefcase, ArrowRight, Filter, Flame } from "lucide-react";

export default function BrowseJobs() {
  const [params, setParams] = useSearchParams();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  const trade = params.get("trade") || "";
  const county = params.get("county") || "";
  const duration = params.get("duration") || "";

  useEffect(() => {
    setLoading(true);
    const q = {};
    if (trade) q.trade = trade;
    if (county) q.county = county;
    if (duration) q.duration = duration;
    api.get("/jobs", { params: q }).then((r) => setJobs(r.data)).finally(() => setLoading(false));
  }, [trade, county, duration]);

  const setFilter = (k, v) => {
    const np = new URLSearchParams(params);
    if (v) np.set(k, v); else np.delete(k);
    setParams(np, { replace: true });
  };

  const clear = () => setParams({}, { replace: true });

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-10">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="text-amber-500 text-xs font-bold tracking-[0.3em] uppercase">Job Board</div>
          <h1 className="display text-4xl md:text-5xl mt-2">Open Shifts</h1>
        </div>
        <div className="text-sm text-neutral-400" data-testid="jobs-count">{jobs.length} jobs</div>
      </div>

      <div className="grid md:grid-cols-12 gap-8">
        {/* Filters */}
        <aside className="md:col-span-3">
          <div className="border border-neutral-800 bg-neutral-900 p-5 sticky top-20">
            <div className="flex items-center gap-2 mb-5">
              <Filter className="w-4 h-4 text-amber-500" />
              <div className="text-xs font-bold uppercase tracking-widest">Filter</div>
            </div>
            <FilterGroup label="Trade" value={trade} options={TRADES} onChange={(v) => setFilter("trade", v)} testid="filter-trade" />
            <FilterGroup label="County" value={county} options={COUNTIES} onChange={(v) => setFilter("county", v)} testid="filter-county" />
            <FilterGroup label="Duration" value={duration} options={DURATIONS} onChange={(v) => setFilter("duration", v)} testid="filter-duration" />
            <button data-testid="filter-clear" onClick={clear} className="mt-4 w-full text-xs font-bold uppercase tracking-widest text-neutral-400 hover:text-amber-500 py-2 border border-neutral-800 hover:border-amber-500 transition-colors">
              Clear
            </button>
          </div>
        </aside>

        {/* List */}
        <section className="md:col-span-9">
          {loading ? (
            <div className="text-neutral-500 text-sm uppercase tracking-widest animate-pulse">Loading jobs…</div>
          ) : jobs.length === 0 ? (
            <EmptyState />
          ) : (
            <ul className="divide-y divide-neutral-800 border-y border-neutral-800" data-testid="jobs-list">
              {jobs.map((j) => (
                <li key={j.id}>
                  <Link
                    to={`/jobs/${j.id}`}
                    data-testid={`job-card-${j.id}`}
                    className={`group block px-1 py-6 hover:bg-neutral-900 transition-colors ${j.is_boosted ? "border-l-2 border-amber-500 pl-4 -mx-1" : ""}`}
                  >
                    <div className="flex items-start gap-6">
                      <div className="hidden sm:grid place-items-center w-14 h-14 bg-neutral-900 border border-neutral-800 group-hover:border-amber-500 shrink-0 transition-colors">
                        <Briefcase className="w-6 h-6 text-amber-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-3 mb-2">
                          {j.is_boosted && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-500 text-neutral-950 text-[10px] font-bold uppercase tracking-widest">
                              <Flame className="w-3 h-3" /> Urgent
                            </span>
                          )}
                          <span className="text-xs font-bold uppercase tracking-widest text-amber-500">{j.trade}</span>
                          <span className="text-xs text-neutral-500">·</span>
                          <span className="text-xs uppercase tracking-wide text-neutral-400 flex items-center gap-1"><MapPin className="w-3 h-3" />{j.county}{j.town ? `, ${j.town}` : ""}{j.counties && j.counties.length > 0 && <span className="text-amber-500 font-bold ml-1">+{j.counties.length}</span>}</span>
                          <span className="text-xs text-neutral-500">·</span>
                          <span className="text-xs uppercase tracking-wide text-neutral-400 flex items-center gap-1"><Clock className="w-3 h-3" />{j.duration}</span>
                        </div>
                        <div className="display text-2xl md:text-3xl group-hover:text-amber-500 transition-colors truncate">{j.title}</div>
                        <div className="text-sm text-neutral-400 mt-2 line-clamp-2">{j.description}</div>
                      </div>
                      <div className="text-right shrink-0">
                        {j.day_rate ? (
                          <div className="display text-3xl text-amber-500 flex items-center justify-end"><Euro className="w-5 h-5" />{j.day_rate}</div>
                        ) : (
                          <div className="text-xs uppercase tracking-widest text-neutral-500">DOE</div>
                        )}
                        <div className="text-[10px] uppercase tracking-widest text-neutral-500 mt-1">per day</div>
                        <div className="mt-3 hidden md:inline-flex items-center gap-1 text-xs font-bold uppercase text-neutral-400 group-hover:text-amber-500">
                          View <ArrowRight className="w-3 h-3" />
                        </div>
                      </div>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}

function FilterGroup({ label, value, options, onChange, testid }) {
  return (
    <div className="mb-5">
      <div className="text-[10px] font-bold uppercase tracking-widest text-neutral-500 mb-2">{label}</div>
      <div className="flex flex-wrap gap-1.5">
        {options.map((o) => {
          const active = value === o;
          return (
            <button
              key={o}
              data-testid={`${testid}-${o.replace(/\s+/g, "-").toLowerCase()}`}
              onClick={() => onChange(active ? "" : o)}
              className={`text-xs px-2.5 py-1.5 uppercase tracking-wide border transition-colors ${active ? "bg-amber-500 text-neutral-950 border-amber-500" : "border-neutral-800 text-neutral-300 hover:border-amber-500 hover:text-amber-500"}`}
            >
              {o}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="border border-neutral-800 p-12 text-center">
      <div className="display text-2xl mb-2">No jobs yet</div>
      <div className="text-neutral-400 text-sm">Try clearing filters, or check back soon.</div>
    </div>
  );
}
